import type { RideStatus, RideType } from './rideTypes';

export const formatPrice = (price: number, currency: string = 'USD'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(price);

/**
 * How an offer stands against the market rate estimated for this ride.
 *
 * Replaces `isGoodDeal(offer, { uber, lyft })`, which compared against numbers
 * this app invented: competitor prices were our own estimate multiplied by
 * 1.2 (Uber) and 1.15 (Lyft), so the comparison could not lose — it was a
 * multiplication wearing a comparison's clothes, and it attributed specific
 * dollar figures to two named companies on no evidence at all.
 *
 * The market rate here is `rides.estimated_market_rate`, stamped when the ride
 * was requested, so both parties are measured against the same number for the
 * life of the negotiation rather than against a figure that moves.
 *
 * `percent` is signed and relative to the market rate: -12 means twelve per
 * cent below it. Returns null when there is no market rate to compare against,
 * which is a real state — `estimated_market_rate` is nullable.
 */
export interface MarketRateComparison {
  /** offer − marketRate. Negative is below market. */
  delta: number;
  /** Signed percentage of the market rate. Negative is below market. */
  percent: number;
  direction: 'below' | 'above' | 'at';
}

export const compareToMarketRate = (
  offer: number,
  marketRate: number | null | undefined,
): MarketRateComparison | null => {
  if (
    marketRate == null ||
    !Number.isFinite(marketRate) ||
    !Number.isFinite(offer) ||
    marketRate <= 0
  ) {
    return null;
  }

  const delta = offer - marketRate;
  const percent = (delta / marketRate) * 100;

  // Round to cents before deciding, so a floating-point tail does not report a
  // penny of difference on two prices that are the same to a human.
  const direction =
    Math.abs(Math.round(delta * 100)) === 0 ? 'at' : delta < 0 ? 'below' : 'above';

  return { delta, percent, direction };
};

/**
 * The fare estimate for a trip, and the number the rest of the system uses.
 *
 * `amount` is the estimate as a number; `price` is the same figure formatted
 * as a range for display. Callers must read `amount` — every one of them used
 * to parse it back out of `price`, and one got it wrong in a way that shipped:
 * `'$18-21'.replace(/[^0-9.]/g, '')` is `'1821'`, so the Expo app stored
 * `estimated_market_rate` as 1821 instead of 18 on every ride it created.
 * A formatted string is a display artefact, not a transport format.
 *
 * Note `price`'s upper bound is always `amount + 3` — a fixed spread, not a
 * modelled one. It carries no information, which is why the vehicle step
 * shows the single figure rather than the range.
 */
export const getPriceEstimate = (
  rideType: RideType,
  distance?: number | null,
  duration?: number | null,
): { price: string; amount: number; time: string; isRouteBased: boolean } => {
  const basePrices = {
    economy: { base: 10, perKm: 0.8, perMin: 0.2 },
    comfort: { base: 15, perKm: 1.2, perMin: 0.3 },
    premium: { base: 25, perKm: 1.8, perMin: 0.5 },
    electric: { base: 18, perKm: 1.4, perMin: 0.4 },
    standard: { base: 12, perKm: 1.0, perMin: 0.25 },
    van: { base: 20, perKm: 1.5, perMin: 0.4 },
  };

  const waitTimes = {
    economy: '12 min',
    comfort: '10 min',
    premium: '7 min',
    electric: '11 min',
    standard: '10 min',
    van: '15 min',
  };

  // Without a route this is a generic figure for the vehicle class, not an
  // estimate for anyone's trip. Callers say so rather than implying precision
  // the number does not have.
  const isRouteBased = Boolean(distance && duration);

  let amount: number;
  if (isRouteBased) {
    const pricing = basePrices[rideType];
    const calculatedPrice =
      pricing.base + distance! * pricing.perKm + duration! * pricing.perMin;
    amount = Math.ceil(calculatedPrice);
  } else {
    const fallbackAmounts = {
      economy: 12,
      comfort: 16,
      premium: 25,
      electric: 18,
      standard: 14,
      van: 20,
    };
    amount = fallbackAmounts[rideType];
  }

  return {
    price: `$${amount}-${amount + 3}`,
    amount,
    time: waitTimes[rideType],
    isRouteBased,
  };
};

/**
 * The statuses in which a ride is under way: a driver is assigned and the trip
 * has not finished. This is the window in which live tracking runs — the
 * driver publishes GPS fixes (`record_driver_location` rejects anything
 * outside this set with 42501) and the rider watches the dot move.
 *
 * Mirrors the `rides.status` CHECK in
 * `supabase/migrations/20260310000002_create_schema.sql`. Note there is no
 * `driver_assigned` status in this schema, despite older docs naming one:
 * `confirmed` is the post-assignment, pre-departure state.
 */
export const ACTIVE_RIDE_STATUSES = [
  'confirmed',
  'driver_en_route',
  'arrived',
  'in_progress',
] as const satisfies readonly RideStatus[];

export type ActiveRideStatus = (typeof ACTIVE_RIDE_STATUSES)[number];

/** True while the ride is assigned and unfinished — see ACTIVE_RIDE_STATUSES. */
export const isActiveRideStatus = (status: string): status is ActiveRideStatus =>
  (ACTIVE_RIDE_STATUSES as readonly string[]).includes(status);
